import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import DatePicker, { registerLocale } from "react-datepicker";
import { ro } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import "./EditEmployees.css";

registerLocale("ro", ro);

const EditEmployees = ({ open, onClose, employeeData }) => {
  // Form initial
  const initialFormData = useMemo(
    () => ({
      first_name: employeeData?.first_name || "",
      last_name: employeeData?.last_name || "",
      department:
        typeof employeeData?.department === "object"
          ? employeeData.department?.id || ""
          : employeeData?.department || "",
      role: employeeData?.role || "",
      contract_date:
        employeeData?.contract_date || new Date().toISOString().split("T")[0],
      phone: employeeData?.phone || "",
      email: employeeData?.email || "",
      adress: employeeData?.adress || "",
      type: employeeData?.type || "",
      status: employeeData?.status || "active",
    }),
    [employeeData]
  );

  // State-uri principale
  const [formData, setFormData] = useState(initialFormData);
  const [departmentsOptions, setDepartmentsOptions] = useState([]); // opțiuni pentru react-select
  const [contractTypesOptions, setContractTypesOptions] = useState([]); // opțiuni pentru react-select
  const [loading, setLoading] = useState(false);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showToast] = useState(false);
  const [toastMessage] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef();

  // opțiuni pentru dropdown "status"
  const statusTypes = useMemo(
    () => [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      { value: "suspended", label: "Suspended" },
    ],
    []
  );

  // Fetch departamente
  const fetchDepartments = useCallback(async () => {
    try {
      setDepartmentsLoading(true);
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const res = await axiosInstance.get("/departments/", {
        signal: abortControllerRef.current.signal,
      });

      // Transformă pentru react-select
      const options = res.data.map((d) => ({
        value: d.id,
        label: d.name,
      }));
      setDepartmentsOptions(options);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err);
        setError("Failed to load departments");
      }
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  // Fetch contract types
  const fetchContractTypes = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/type-contract/");

      // Transformă pentru react-select
      const options = res.data.map((c) => ({
        value: c.id,
        label: c.name || c.type,
      }));
      setContractTypesOptions(options);
    } catch (err) {
      console.error("❌ Eroare la încărcarea tipurilor de contract:", err);
    }
  }, []);

  // Când se deschide modalul
  useEffect(() => {
    if (open) {
      setFormData(initialFormData);
      setFieldErrors({});
      fetchDepartments();
      fetchContractTypes();

      const base = axiosInstance.defaults.baseURL?.replace(/\/$/, "") || "";

      let avatarUrl = null;
      if (employeeData?.avatar) {
        const raw = employeeData.avatar;

        if (raw.startsWith("http")) {
          avatarUrl = raw;
        } else if (raw.startsWith("/")) {
          avatarUrl = `${base}${raw}`;
        } else {
          avatarUrl = `${base}/media/avatars/${raw}`;
        }
      }

      setAvatarPreview(avatarUrl);

      setError("");
    }
  }, [open, initialFormData, fetchDepartments, fetchContractTypes, employeeData]);

  // Gestionare modificare câmpuri de formular
  const handleChange = useCallback(
    (field) => (e) => {
      let value = e.target.value;

      // Curățare input telefon — doar cifre, max 15 caractere
      if (field === "phone") {
        value = value.replace(/\D/g, "");
        if (value.length > 15) {
          value = value.slice(0, 15);
        }
      }

      setFormData((prev) => ({ ...prev, [field]: value }));
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    },
    []
  );

  // Gestionare modificare select-uri cu react-select
  const handleSelectChange = useCallback((field) => (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const handleStatusChange = useCallback((selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, status: value }));
    setFieldErrors((prev) => ({ ...prev, status: "" }));
  }, []);

  // Gestionare modificare date
  const handleDateChange = useCallback((date) => {
    setFormData((prev) => ({
      ...prev,
      contract_date: date ? date.toISOString().split("T")[0] : "",
    }));
    setFieldErrors((prev) => ({ ...prev, contract_date: "" }));
  }, []);

  // Input simplu
  const handleCancel = useCallback(() => {
    onClose(false);
  }, [onClose]);

  // Avatar upload
  const handleAvatarClick = useCallback(() => {
    if (!avatarUploading) fileInputRef.current?.click();
  }, [avatarUploading]);

  const handleAvatarChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      alert("Please select a valid image (JPG, PNG, WEBP).");
      e.target.value = "";
      return;
    }

    const max = 10 * 1024 * 1024;
    if (file.size > max) {
      alert("Image too large (max 10MB).");
      e.target.value = "";
      return;
    }

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  }, []);

  // Validare formular
  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.first_name.trim()) errors.first_name = "First name is required";
    if (!formData.last_name.trim()) errors.last_name = "Last name is required";
    if (!formData.department) errors.department = "Department is required";
    if (!formData.role.trim()) errors.role = "Role is required";
    if (!formData.contract_date) errors.contract_date = "Start date is required";
    if (!formData.phone.trim()) errors.phone = "Phone number is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!formData.type) errors.type = "Contract type is required";
    if (!formData.status) errors.status = "Status is required";

    // Validare format email
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Submit update
  const handleSubmit = useCallback(async () => {
    if (!employeeData?.id) {
      setError("Invalid employee ID");
      return;
    }

    if (!validateForm()) return;

    setError("");
    setLoading(true);
    setAvatarUploading(true);

    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "department") {
          const depVal = parseInt(value, 10);
          fd.append("department", isNaN(depVal) ? "" : depVal);
        } else if (key === "type") {
          const typeVal = parseInt(value, 10);
          fd.append("type", isNaN(typeVal) ? "" : typeVal);
        } else {
          fd.append(key, value ?? "");
        }
      });

      if (avatarFile) fd.append("avatar", avatarFile);

      const res = await axiosInstance.put(`/employees/${employeeData.id}/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.status === 200) {
        onClose(true, "✅ Employee updated successfully!");
      }
    } catch (err) {
      console.error("❌ Edit failed:", err.response?.data || err);
      setError("Failed to update employee");
    } finally {
      setLoading(false);
      setAvatarUploading(false);
    }
  }, [employeeData, formData, avatarFile, onClose, validateForm]);

  // Stiluri custom pentru react-select cu suport pentru erori
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      border: fieldErrors.department && state.selectProps.name === 'department' ? '1px solid #d32f2f' :
        fieldErrors.type && state.selectProps.name === 'type' ? '1px solid #d32f2f' :
          fieldErrors.status && state.selectProps.name === 'status' ? '1px solid #d32f2f' :
            state.isFocused ? '1px solid #007BFF' : '1px solid #000',
      boxShadow: 'none',
      '&:hover': {
        border: fieldErrors.department && state.selectProps.name === 'department' ? '1px solid #d32f2f' :
          fieldErrors.type && state.selectProps.name === 'type' ? '1px solid #d32f2f' :
            fieldErrors.status && state.selectProps.name === 'status' ? '1px solid #d32f2f' :
              state.isFocused ? '1px solid #007BFF' : '1px solid #000'
      },
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      minHeight: '38px',
      height: '38px',
      backgroundColor: state.isFocused ? '#fff' : '#fff',
      display: 'flex',
      alignItems: 'center',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 8px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
    }),
    input: (base) => ({
      ...base,
      color: '#1a1a1a',
      margin: '0',
      padding: '0',
      '& input': {
        boxShadow: 'none !important',
        border: 'none !important',
        outline: 'none !important',
        padding: '0',
        margin: '0',
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#999',
      fontSize: '14px',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
    }),
    singleValue: (base) => ({
      ...base,
      color: '#1a1a1a',
      fontSize: '14px',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      fontSize: '14px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#e6f2ff' : state.isFocused ? '#f0f0f0' : '#fff',
      color: state.isSelected ? '#006ce4' : '#1a1a1a',
      fontSize: '14px',
      '&:active': {
        backgroundColor: '#e6f2ff',
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#666',
      '&:hover': {
        color: '#333',
      },
      padding: '4px 8px',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#666',
      '&:hover': {
        color: '#333',
      },
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '36px',
      display: 'flex',
      alignItems: 'center',
    }),
    indicatorSeparator: (base) => ({
      ...base,
      marginTop: '8px',
      marginBottom: '8px',
    }),
  };

  if (!open) return null;

  return (
    <>
      {showToast && <div className="global-toast">{toastMessage}</div>}

      <div className="page-edit-employee">
        <div className="edit-employee-page">
          <div className="modal-overlay">
            <div className="modal">
              {/* Header */}
              <div className="modal-header">
                <h2>Edit Employee</h2>
                <button className="close-btn" onClick={handleCancel}>×</button>
              </div>

              <hr className="header-divider" />

              {/* Error */}
              {error && <div className="alert error">{error}</div>}

              <div className="form">
                {/* Avatar */}
                <div className="avatar-picker-container">
                  <div className="avatar-wrapper">
                    <div className="avatar-circle">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Avatar preview" />
                      ) : (
                        <div className="avatar-placeholder">
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>
                      )}
                      {avatarUploading && (
                        <div className="avatar-overlay"><div className="spinner-small"></div></div>
                      )}
                      <button
                        type="button"
                        className="avatar-plus-btn"
                        onClick={handleAvatarClick}
                        disabled={avatarUploading}
                      >
                        +
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden-file-input"
                        onChange={handleAvatarChange}
                      />
                    </div>

                    <div className="employee-name-below">
                      <h3>
                        {`${employeeData?.first_name || ""} ${employeeData?.last_name || ""}`.trim() ||
                          "Unnamed Employee"}
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Contract + status */}
                <div className="form-row">
                  <div className="form-field">
                    <label>Contract <span className="required">*</span></label>
                    <div className={`select-wrapper ${fieldErrors.type ? "field-error-border" : ""}`}>
                      <Select
                        name="type"
                        value={contractTypesOptions.find(option => option.value === parseInt(formData.type))}
                        onChange={handleSelectChange("type")}
                        options={contractTypesOptions}
                        placeholder="Select contract type"
                        className="multiselect-field"
                        classNamePrefix="select"
                        isSearchable={true}
                        isClearable={true}
                        required={false}
                        styles={customSelectStyles}
                      />
                    </div>
                    {fieldErrors.type && <span className="field-error">{fieldErrors.type}</span>}
                  </div>

                  <div className="form-field">
                    <label>Status <span className="required">*</span></label>
                    <div className={`select-wrapper ${fieldErrors.status ? "field-error-border" : ""}`}>
                      <Select
                        name="status"
                        value={statusTypes.find(option => option.value === formData.status)}
                        onChange={handleStatusChange}
                        options={statusTypes}
                        placeholder="Select status"
                        className="multiselect-field"
                        classNamePrefix="select"
                        isSearchable={true}
                        isClearable={true}
                        required={false}
                        styles={customSelectStyles}
                      />
                    </div>
                    {fieldErrors.status && <span className="field-error">{fieldErrors.status}</span>}
                  </div>
                </div>

                {/* Name */}
                <div className="form-row">
                  <div className="form-field">
                    <label>First name <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter first name"
                      value={formData.first_name}
                      onChange={handleChange("first_name")}
                      className={fieldErrors.first_name ? "field-error-border" : ""}
                      required
                    />
                    {fieldErrors.first_name && <span className="field-error">{fieldErrors.first_name}</span>}
                  </div>

                  <div className="form-field">
                    <label>Last name <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter last name"
                      value={formData.last_name}
                      onChange={handleChange("last_name")}
                      className={fieldErrors.last_name ? "field-error-border" : ""}
                      required
                    />
                    {fieldErrors.last_name && <span className="field-error">{fieldErrors.last_name}</span>}
                  </div>
                </div>

                {/* Department + role */}
                <div className="form-row">
                  <div className="form-field">
                    <label>Department <span className="required">*</span></label>
                    {departmentsLoading ? (
                      <div className="skeleton"></div>
                    ) : (
                      <div className={`select-wrapper ${fieldErrors.department ? "field-error-border" : ""}`}>
                        <Select
                          name="department"
                          value={departmentsOptions.find(option => option.value === parseInt(formData.department))}
                          onChange={handleSelectChange("department")}
                          options={departmentsOptions}
                          placeholder="Select department"
                          className="multiselect-field"
                          classNamePrefix="select"
                          isSearchable={true}
                          isClearable={true}
                          required={false}
                          styles={customSelectStyles}
                        />
                      </div>
                    )}
                    {fieldErrors.department && <span className="field-error">{fieldErrors.department}</span>}
                  </div>

                  <div className="form-field">
                    <label>Role <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter role"
                      value={formData.role}
                      onChange={handleChange("role")}
                      className={fieldErrors.role ? "field-error-border" : ""}
                      required
                    />
                    {fieldErrors.role && <span className="field-error">{fieldErrors.role}</span>}
                  </div>
                </div>

                {/* Date + phone */}
                <div className="form-row">
                  <div className="form-field">
                    <label>Start Date <span className="required">*</span></label>
                    <div className={`datepicker-container ${fieldErrors.contract_date ? "field-error-border" : ""}`}>
                      <DatePicker
                        selected={
                          formData.contract_date
                            ? new Date(formData.contract_date)
                            : null
                        }
                        onChange={handleDateChange}
                        dateFormat="dd.MM.yyyy"
                        locale="ro"
                        placeholderText="dd.mm.yyyy"
                        className={`datepicker-input-with-icon ${fieldErrors.contract_date ? "field-error-border" : ""}`}
                        calendarClassName="custom-calendar"
                        popperPlacement="bottom-start"
                      />

                      <span
                        className="calendar-icon-inside"
                        onClick={() =>
                          document.querySelector(".datepicker-input-with-icon")?.focus()
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </span>
                    </div>

                    {fieldErrors.contract_date && (
                      <div className="field-error-container">
                        <span className="field-error">{fieldErrors.contract_date}</span>
                      </div>
                    )}
                  </div>

                  <div className="form-field phone-field">
                    <label>Phone number <span className="required">*</span></label>
                    <div className={`input-with-icon ${fieldErrors.phone ? "field-error-border" : ""}`}>
                      <span className="phone-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" viewBox="0 0 24 24">
                          <path d="m14.662 23.038.012.007c2.46 1.566 5.71 1.21 7.792-.873l.774-.774a2.596 2.596 0 0 0 0-3.669l-3.26-3.26a2.596 2.596 0 0 0-3.67 0 1.093 1.093 0 0 1-1.546.002l-.001-.001-5.219-5.22a1.096 1.096 0 0 1 0-1.548 2.593 2.593 0 0 0 .002-3.666L6.284.77a2.596 2.596 0 0 0-3.669 0l-.774.774A6.285 6.285 0 0 0 .982 9.36L1 9.386a50.7 50.7 0 0 0 13.62 13.625z" />
                        </svg>
                      </span>
                      <input
                        type="text"
                        placeholder="Enter phone number"
                        value={formData.phone}
                        onChange={handleChange("phone")}
                        className={fieldErrors.phone ? "field-error-border" : ""}
                        required
                      />
                    </div>
                    {fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
                  </div>
                </div>

                {/* Email - CÂMP NOU ADĂUGAT */}
                <div className="form-field">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleChange("email")}
                    className={fieldErrors.email ? "field-error-border" : ""}
                    required
                  />
                  {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
                </div>

                {/* Address */}
                <div className="form-field">
                  <label>Address</label>
                  <textarea
                    className="address-input"
                    placeholder="Enter address"
                    value={formData.adress}
                    onChange={handleChange("adress")}
                  />
                </div>

                {/* Buttons */}
                <div className="form-buttons">
                  <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
                  <button
                    className={`submit-btn ${loading ? "disabled" : ""}`}
                    onClick={!loading ? handleSubmit : undefined}
                    disabled={loading || departmentsLoading || avatarUploading}
                  >
                    {loading ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditEmployees;